/**
 * ApplicationScope Hooks - TanStack Query hooks for application scope operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { query, mutate } from '@/lib/graphql';
import {
  GET_VISIBLE_APPLICATION_SCOPES,
  GET_APPLICATION_SCOPE,
  GET_APPLICATION_SCOPES,
  CREATE_APPLICATION_SCOPE,
  UPDATE_APPLICATION_SCOPE,
  REORDER_APPLICATION_SCOPES,
  type GetVisibleApplicationScopesResponse,
  type GetApplicationScopeResponse,
  type GetApplicationScopesResponse,
  type CreateApplicationScopePayload,
  type UpdateApplicationScopePayload,
  type ReorderApplicationScopesPayload,
  type ApplicationScope,
  type CreateApplicationScopeInput,
  type UpdateApplicationScopeInput,
  type ReorderApplicationScopeInput,
} from '@/lib/graphql/applicationscope';

/**
 * Visible application scopes for the caller — the source of the sidebar's nav sections.
 *
 * Pass previewRoleId to derive the scopes from a previewed role's capability bundle
 * instead of the caller's own (CDEV-1454), so nav narrows under a role preview. The
 * server honours it only for callers holding sec:role-read.
 *
 * The preview role is part of the query key, so the real and previewed sets never
 * overwrite each other in the cache. The full administrative registry uses its
 * own applicationScopeRegistry key.
 */
export function useVisibleApplicationScopes(options?: {
  enabled?: boolean;
  previewRoleId?: string | null;
}) {
  const previewRoleId = options?.previewRoleId ?? null;
  return useQuery({
    queryKey: ['applicationScopes', previewRoleId],
    queryFn: async () => {
      const response = await query<GetVisibleApplicationScopesResponse>(
        GET_VISIBLE_APPLICATION_SCOPES,
        { previewRoleId: previewRoleId ?? undefined },
      );
      return response.visibleApplicationScopes;
    },
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

export function useApplicationScope(scopeId: string | null) {
  return useQuery({
    queryKey: ['applicationScope', scopeId],
    queryFn: async () => {
      if (!scopeId) return null;
      const response = await query<GetApplicationScopeResponse>(GET_APPLICATION_SCOPE, { scopeId });
      return response.applicationScope;
    },
    enabled: !!scopeId,
  });
}

export function useApplicationScopes() {
  return useQuery({
    queryKey: ['applicationScopeRegistry'],
    queryFn: async () => {
      const response = await query<GetApplicationScopesResponse>(GET_APPLICATION_SCOPES);
      return response.applicationScopes;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateApplicationScope() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateApplicationScopeInput) => {
      const response = await mutate<CreateApplicationScopePayload>(CREATE_APPLICATION_SCOPE, { input });
      return response.createApplicationScope.scope;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applicationScopes'] });
      queryClient.invalidateQueries({ queryKey: ['applicationScopeRegistry'] });
    },
  });
}

export function useUpdateApplicationScope() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { scopeId: string } & UpdateApplicationScopeInput) => {
      const { scopeId, ...updateInput } = input;
      const response = await mutate<UpdateApplicationScopePayload>(UPDATE_APPLICATION_SCOPE, { scopeId, input: updateInput });
      return response.updateApplicationScope.scope;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applicationScopes'] });
      queryClient.invalidateQueries({ queryKey: ['applicationScopeRegistry'] });
    },
  });
}

export function useReorderApplicationScopes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReorderApplicationScopeInput[]) => {
      const response = await mutate<ReorderApplicationScopesPayload>(REORDER_APPLICATION_SCOPES, { input });
      return response.reorderApplicationScopes.scopes;
    },
    onMutate: async (input) => {
      // Cancel in-flight refetches so they don't overwrite optimistic update.
      await queryClient.cancelQueries({ queryKey: ['applicationScopeRegistry'] });

      // Snapshot the complete registry for rollback. The sidebar projection has
      // a separate cache because it is capability-filtered.
      const previous = queryClient.getQueryData<ApplicationScope[]>(['applicationScopeRegistry']);

      // Build a lookup of new orderHints keyed by scopeId.
      const orderMap = new Map(input.map((u) => [u.scopeId, u.orderHint]));

      // Optimistically update the cache with new orderHints and re-sort.
      queryClient.setQueryData<ApplicationScope[]>(['applicationScopeRegistry'], (old) => {
        if (!old) return old;
        return old
          .map((s) => (orderMap.has(s.scopeId) ? { ...s, orderHint: orderMap.get(s.scopeId)! } : s))
          .sort((a, b) => (a.orderHint ?? 0) - (b.orderHint ?? 0));
      });

      return { previous };
    },
    onError: (_err, _input, context) => {
      // Roll back to the snapshot on failure.
      if (context?.previous) {
        queryClient.setQueryData(['applicationScopeRegistry'], context.previous);
      }
    },
    onSettled: () => {
      // Refetch to ensure server state is authoritative.
      queryClient.invalidateQueries({ queryKey: ['applicationScopes'] });
      queryClient.invalidateQueries({ queryKey: ['applicationScopeRegistry'] });
    },
  });
}
